import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { resolveTheme } from '../constants/themes';

const API_URL = process.env.REACT_APP_API_URL || '';

function BandAbout() {
  const { bandSlug } = useParams();
  const [branding, setBranding] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/settings`),
      axios.get(`${API_URL}/api/band-members`),
    ]).then(([brandingRes, membersRes]) => {
      setBranding(brandingRes.data);
      setMembers(membersRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;

  const theme     = resolveTheme(branding || {});
  const primary   = theme.primary;
  const secondary = theme.secondary;
  const headerBg  = theme.navGradient;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', minHeight: '100dvh', background: theme.background, color: theme.text }}>
      {/* Header */}
      <div style={{ background: headerBg, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Link
          to={`/gig/${bandSlug}`}
          style={{ color: 'white', textDecoration: 'none', fontSize: '1.3rem', lineHeight: 1, flexShrink: 0 }}
        >
          ←
        </Link>

        {/* 2-col grid: logo spans both rows, name + tagline stacked */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: branding?.logo_url ? 'auto 1fr' : '1fr',
          gridTemplateRows: 'auto auto',
          columnGap: '0.75rem',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
        }}>
          {branding?.logo_url && (
            <img
              src={branding.logo_url}
              alt="Band logo"
              style={{
                gridRow: '1 / 3', gridColumn: '1',
                width: '44px', height: '44px',
                objectFit: 'contain', borderRadius: '6px',
                background: 'rgba(255,255,255,0.15)',
                flexShrink: 0,
              }}
            />
          )}
          <div style={{
            gridRow: '1', gridColumn: branding?.logo_url ? '2' : '1',
            color: 'white', fontWeight: '700', fontSize: '1.05rem', lineHeight: 1.2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {branding?.band_name || 'Meet the Band'}
          </div>
          {branding?.tagline && (
            <div style={{
              gridRow: '2', gridColumn: branding?.logo_url ? '2' : '1',
              color: 'rgba(255,255,255,0.72)', fontSize: '0.78rem', marginTop: '0.1rem',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {branding.tagline}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1rem' }}>
        <p style={{
          textTransform: 'uppercase', letterSpacing: '0.1em',
          fontWeight: '700', fontSize: '0.72rem', color: theme.textMuted,
          margin: '0.25rem 0 1rem',
        }}>
          Meet the Band
        </p>

        {members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: theme.textMuted }}>
            No band member info available yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {members.map(m => (
              <div
                key={m.id}
                style={{
                  background: theme.surface, borderRadius: '12px', padding: '1rem',
                  display: 'flex', gap: '1rem', alignItems: 'flex-start',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  border: `1px solid ${theme.border}`,
                }}
              >
                {/* Avatar */}
                {m.photo_url ? (
                  <img
                    src={m.photo_url}
                    alt={m.name}
                    style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '50%',
                    background: headerBg, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'white',
                    fontWeight: '700', fontSize: '1.4rem', flexShrink: 0,
                  }}>
                    {m.name[0].toUpperCase()}
                  </div>
                )}

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.1rem', color: theme.text }}>{m.name}</div>
                  {m.role && (
                    <div style={{ color: primary, fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.35rem' }}>
                      {m.role}
                    </div>
                  )}
                  {m.bio && (
                    <p style={{ margin: 0, color: theme.textSecondary, fontSize: '0.875rem', lineHeight: 1.55 }}>
                      {m.bio}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BandAbout;
